import { useState, useEffect } from "react";
import DashboardLayout from "@/components/DashboardLayout";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Separator } from "@/components/ui/separator";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  BookOpen, Plus, ThumbsUp, ThumbsDown, Eye, MessageSquare,
  Trophy, Star, Crown, Flame, Award, TrendingUp, Sparkles,
  Edit, Send, Search
} from "lucide-react";
import axios from "axios";
import { API } from "@/App";
import { toast } from "sonner";
import { useAuth } from "@/App";

// Points configuration (synced with backend)
const POINTS_CONFIG = {
  article_created: 50,
  article_published: 100,
  article_upvoted: 5,
  helpful_answer: 10,
  comment_added: 2
};

export default function WikiHowPage() {
  const { user } = useAuth();
  const [articles, setArticles] = useState([]);
  const [selectedArticle, setSelectedArticle] = useState(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showArticleModal, setShowArticleModal] = useState(false);
  const [leaderboard, setLeaderboard] = useState([]);
  const [myStats, setMyStats] = useState(null);
  const [badges, setBadges] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [sortBy, setSortBy] = useState("popular");
  const [loading, setLoading] = useState(true);

  const [newArticle, setNewArticle] = useState({
    title: "",
    content: "",
    category: "Workflows",
    tags: []
  });

  useEffect(() => {
    fetchArticles();
    fetchLeaderboard();
    fetchMyStats();
    fetchBadges();
  }, [sortBy]);

  const fetchArticles = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/wikihow/articles?sort=${sortBy}&limit=50`);
      setArticles(response.data.articles);
    } catch (error) {
      console.error('Error fetching articles:', error);
      toast.error("Failed to load articles");
    } finally {
      setLoading(false);
    }
  };

  const fetchLeaderboard = async () => {
    try {
      const response = await axios.get(`${API}/wikihow/leaderboard?limit=10`);
      setLeaderboard(response.data.leaderboard);
    } catch (error) {
      console.error('Error fetching leaderboard:', error);
    }
  };

  const fetchMyStats = async () => {
    try {
      const response = await axios.get(`${API}/wikihow/my-stats`, { withCredentials: true });
      setMyStats(response.data);
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  };

  const fetchBadges = async () => {
    try {
      const response = await axios.get(`${API}/wikihow/badges`);
      setBadges(response.data.badges);
    } catch (error) {
      console.error('Error fetching badges:', error);
    }
  };

  const createArticle = async () => {
    try {
      const response = await axios.post(`${API}/wikihow/articles`, newArticle, { withCredentials: true });
      toast.success(`Article created! +${response.data.points_earned} points`);
      setShowCreateModal(false);
      setNewArticle({ title: "", content: "", category: "Workflows", tags: [] });
      fetchArticles();
      fetchMyStats();
    } catch (error) {
      console.error('Error creating article:', error);
      toast.error("Failed to create article");
    }
  };

  const publishArticle = async (articleId) => {
    try {
      const response = await axios.post(`${API}/wikihow/articles/${articleId}/publish`, {}, { withCredentials: true });
      toast.success(`Published! +${response.data.points_earned} points`);
      fetchArticles();
      fetchMyStats();
    } catch (error) {
      console.error('Error publishing article:', error);
      toast.error("Failed to publish article");
    }
  };

  const voteOnArticle = async (articleId, voteType) => {
    try {
      await axios.post(`${API}/wikihow/articles/${articleId}/vote`, { vote_type: voteType }, { withCredentials: true });
      toast.success(voteType === "upvote" ? "Upvoted!" : "Downvoted");
      fetchArticles();
    } catch (error) {
      console.error('Error voting:', error);
      toast.error("Failed to vote");
    }
  };

  const openArticle = async (article) => {
    try {
      const response = await axios.get(`${API}/wikihow/articles/${article.article_id}`);
      setSelectedArticle(response.data);
      setShowArticleModal(true);
    } catch (error) {
      console.error('Error fetching article:', error);
      toast.error("Failed to load article");
    }
  };

  const getBadgeIcon = (badgeId) => {
    const badge = badges.find(b => b.id === badgeId);
    return badge?.icon || "🎯";
  };

  const filteredArticles = articles.filter(article =>
    article.title?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    article.content?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <DashboardLayout>
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6" data-testid="wikihow-page">
        {/* Main Content */}
        <div className="lg:col-span-2 space-y-6">
          {/* Header */}
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
            <div>
              <h1 className="text-3xl font-heading font-semibold text-foreground flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-green-500 to-emerald-500 flex items-center justify-center">
                  <BookOpen className="w-6 h-6 text-white" />
                </div>
                WikiHow Community
              </h1>
              <p className="text-muted-foreground mt-1">
                Step-by-step guides created by our community
              </p>
            </div>
            <Button
              onClick={() => setShowCreateModal(true)}
              data-testid="create-article-btn"
              className="flex items-center gap-2"
            >
              <Plus className="w-4 h-4" />
              Create Article
            </Button>
          </div>

          {/* My Stats */}
          {myStats && myStats.total_points > 0 && (
            <Card className="glass-card border-2 border-primary/20">
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 rounded-full bg-gradient-to-br from-yellow-500 to-orange-500 flex items-center justify-center">
                      <Trophy className="w-6 h-6 text-white" />
                    </div>
                    <div>
                      <p className="font-semibold text-foreground">Your Stats</p>
                      <div className="flex items-center gap-4 text-sm text-muted-foreground mt-1">
                        <span>🎯 {myStats.total_points} points</span>
                        <span>📋 {myStats.articles_published} articles</span>
                        <span>🏆 Rank #{myStats.rank}</span>
                      </div>
                    </div>
                  </div>
                  <div className="flex gap-1">
                    {myStats.badges?.map((badge, i) => (
                      <span key={i} className="text-2xl" title={badge.name}>
                        {badge.icon}
                      </span>
                    ))}
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Search and Sort */}
          <div className="flex gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                placeholder="Search articles..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10"
              />
            </div>
            <Select value={sortBy} onValueChange={setSortBy}>
              <SelectTrigger className="w-40">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="popular">🔥 Popular</SelectItem>
                <SelectItem value="recent">🕒 Recent</SelectItem>
                <SelectItem value="top_rated">⭐ Top Rated</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Articles List */}
          {loading ? (
            <div className="space-y-4">
              {[1, 2, 3].map(i => (
                <div key={i} className="h-32 bg-muted/50 rounded-xl animate-pulse" />
              ))}
            </div>
          ) : filteredArticles.length > 0 ? (
            <div className="space-y-4">
              {filteredArticles.map((article) => (
                <Card
                  key={article.article_id}
                  className="glass-card border-border hover:border-primary/30 transition-all cursor-pointer"
                  onClick={() => openArticle(article)}
                  data-testid={`article-${article.article_id}`}
                >
                  <CardHeader>
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-2">
                          <Badge variant="outline" className="capitalize">
                            {article.category}
                          </Badge>
                          {article.tags?.slice(0, 2).map((tag, i) => (
                            <Badge key={i} variant="secondary" className="text-xs">
                              {tag}
                            </Badge>
                          ))}
                        </div>
                        <CardTitle className="font-heading text-lg">
                          {article.title}
                        </CardTitle>
                        <p className="text-sm text-muted-foreground mt-2 line-clamp-2">
                          {article.content?.substring(0, 150)}...
                        </p>
                      </div>
                    </div>
                    
                    <div className="flex items-center justify-between mt-4">
                      <div className="flex items-center gap-2">
                        <Avatar className="w-6 h-6">
                          <AvatarImage src={article.author_picture} />
                          <AvatarFallback className="text-xs">
                            {article.author_name?.charAt(0) || 'U'}
                          </AvatarFallback>
                        </Avatar>
                        <span className="text-xs text-muted-foreground">
                          {article.author_name}
                        </span>
                      </div>
                      <div className="flex items-center gap-4 text-xs text-muted-foreground">
                        <span className="flex items-center gap-1">
                          <Eye className="w-3 h-3" />
                          {article.views || 0}
                        </span>
                        <span className="flex items-center gap-1">
                          <ThumbsUp className="w-3 h-3" />
                          {article.upvotes || 0}
                        </span>
                        <span className="flex items-center gap-1">
                          <MessageSquare className="w-3 h-3" />
                          {article.comments?.length || 0}
                        </span>
                      </div>
                    </div>
                  </CardHeader>
                </Card>
              ))}
            </div>
          ) : (
            <div className="text-center py-16">
              <BookOpen className="w-16 h-16 text-muted-foreground mx-auto mb-4 opacity-50" />
              <p className="text-lg font-medium text-foreground">No articles yet</p>
              <p className="text-sm text-muted-foreground mt-2">
                Be the first to create a helpful guide!
              </p>
              <Button onClick={() => setShowCreateModal(true)} className="mt-4">
                <Plus className="w-4 h-4 mr-2" />
                Create First Article
              </Button>
            </div>
          )}
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          {/* Leaderboard */}
          <Card className="glass-card border-border">
            <CardHeader>
              <CardTitle className="font-heading text-lg flex items-center gap-2">
                <Trophy className="w-5 h-5 text-yellow-500" />
                Top Contributors
              </CardTitle>
            </CardHeader>
            <CardContent>
              <ScrollArea className="h-96">
                <div className="space-y-3">
                  {leaderboard.map((contributor, index) => (
                    <div
                      key={contributor.user_id}
                      className="flex items-center gap-3 p-2 rounded-lg hover:bg-muted/30 transition-colors"
                    >
                      <div className="flex items-center justify-center w-8 h-8 rounded-full font-bold text-sm"
                           style={{
                             backgroundColor: index === 0 ? '#FFD700' : index === 1 ? '#C0C0C0' : index === 2 ? '#CD7F32' : 'transparent'
                           }}>
                        {index < 3 ? (
                          <span className="text-white">#{index + 1}</span>
                        ) : (
                          <span className="text-muted-foreground">#{index + 1}</span>
                        )}
                      </div>
                      <Avatar className="w-8 h-8">
                        <AvatarImage src={contributor.picture} />
                        <AvatarFallback className="text-xs">
                          {contributor.name?.charAt(0) || 'U'}
                        </AvatarFallback>
                      </Avatar>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-foreground truncate">
                          {contributor.name || 'Anonymous'}
                        </p>
                        <p className="text-xs text-muted-foreground">
                          {contributor.total_points} points
                        </p>
                      </div>
                      <div className="flex gap-0.5">
                        {contributor.badges?.slice(0, 3).map((badgeId, i) => (
                          <span key={i} className="text-sm">
                            {getBadgeIcon(badgeId)}
                          </span>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              </ScrollArea>
            </CardContent>
          </Card>

          {/* Recognition Badges */}
          <Card className="glass-card border-border">
            <CardHeader>
              <CardTitle className="font-heading text-lg flex items-center gap-2">
                <Award className="w-5 h-5 text-purple-500" />
                Badges
              </CardTitle>
              <CardDescription>Earn badges by contributing</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 gap-2">
                {badges.slice(0, 8).map((badge) => (
                  <div
                    key={badge.id}
                    className="p-2 rounded-lg bg-muted/30 text-center"
                    title={badge.description}
                  >
                    <span className="text-2xl block mb-1">{badge.icon}</span>
                    <p className="text-xs font-medium text-foreground">{badge.name}</p>
                    <p className="text-xs text-muted-foreground">
                      {badge.points_required > 0 ? `${badge.points_required} pts` : 'Special'}
                    </p>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Create Article Modal */}
      <Dialog open={showCreateModal} onOpenChange={setShowCreateModal}>
        <DialogContent className="max-w-3xl">
          <DialogHeader>
            <DialogTitle className="font-heading">Create WikiHow Article</DialogTitle>
            <DialogDescription>
              Share your knowledge and earn points! ({POINTS_CONFIG.article_created} pts for creating + {POINTS_CONFIG.article_published} pts for publishing)
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div>
              <Label htmlFor="title">Article Title</Label>
              <Input
                id="title"
                placeholder="How to..."
                value={newArticle.title}
                onChange={(e) => setNewArticle({...newArticle, title: e.target.value})}
                data-testid="article-title-input"
              />
            </div>

            <div>
              <Label htmlFor="category">Category</Label>
              <Select
                value={newArticle.category}
                onValueChange={(value) => setNewArticle({...newArticle, category: value})}
              >
                <SelectTrigger id="category">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="Workflows">Workflows</SelectItem>
                  <SelectItem value="Integrations">Integrations</SelectItem>
                  <SelectItem value="AI Features">AI Features</SelectItem>
                  <SelectItem value="Advanced">Advanced</SelectItem>
                  <SelectItem value="Getting Started">Getting Started</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label htmlFor="content">Content (Markdown supported)</Label>
              <Textarea
                id="content"
                placeholder="Write your step-by-step guide...

1. First step
2. Second step
3. Third step

Tip: Be clear and detailed!"
                value={newArticle.content}
                onChange={(e) => setNewArticle({...newArticle, content: e.target.value})}
                rows={12}
                data-testid="article-content-input"
              />
            </div>

            <div>
              <Label htmlFor="tags">Tags (comma separated)</Label>
              <Input
                id="tags"
                placeholder="workflow, automation, beginner"
                onChange={(e) => setNewArticle({
                  ...newArticle,
                  tags: e.target.value.split(',').map(t => t.trim()).filter(t => t)
                })}
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreateModal(false)}>
              Cancel
            </Button>
            <Button
              onClick={createArticle}
              disabled={!newArticle.title || !newArticle.content}
              data-testid="submit-article-btn"
            >
              <Send className="w-4 h-4 mr-2" />
              Create Article
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* View Article Modal */}
      <Dialog open={showArticleModal} onOpenChange={setShowArticleModal}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-hidden flex flex-col">
          {selectedArticle && (
            <>
              <DialogHeader>
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1">
                    <DialogTitle className="font-heading text-2xl mb-2">
                      {selectedArticle.title}
                    </DialogTitle>
                    <div className="flex items-center gap-3 text-sm text-muted-foreground">
                      <div className="flex items-center gap-2">
                        <Avatar className="w-6 h-6">
                          <AvatarImage src={selectedArticle.author_picture} />
                          <AvatarFallback className="text-xs">
                            {selectedArticle.author_name?.charAt(0) || 'U'}
                          </AvatarFallback>
                        </Avatar>
                        <span>{selectedArticle.author_name}</span>
                      </div>
                      <span>•</span>
                      <span>👁 {selectedArticle.views} views</span>
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => voteOnArticle(selectedArticle.article_id, "upvote")}
                      className="flex items-center gap-1"
                    >
                      <ThumbsUp className="w-4 h-4" />
                      {selectedArticle.upvotes || 0}
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => voteOnArticle(selectedArticle.article_id, "downvote")}
                      className="flex items-center gap-1"
                    >
                      <ThumbsDown className="w-4 h-4" />
                      {selectedArticle.downvotes || 0}
                    </Button>
                  </div>
                </div>
              </DialogHeader>

              <ScrollArea className="flex-1 pr-4">
                <div className="prose prose-sm dark:prose-invert max-w-none">
                  <p className="whitespace-pre-wrap text-foreground">
                    {selectedArticle.content}
                  </p>
                </div>

                {selectedArticle.tags && selectedArticle.tags.length > 0 && (
                  <div className="mt-6">
                    <Separator className="mb-4" />
                    <div className="flex flex-wrap gap-2">
                      {selectedArticle.tags.map((tag, i) => (
                        <Badge key={i} variant="secondary">
                          {tag}
                        </Badge>
                      ))}
                    </div>
                  </div>
                )}

                {selectedArticle.comments && selectedArticle.comments.length > 0 && (
                  <div className="mt-6">
                    <Separator className="mb-4" />
                    <h3 className="font-semibold text-foreground mb-3">Comments</h3>
                    <div className="space-y-3">
                      {selectedArticle.comments.map((comment) => (
                        <div key={comment.comment_id} className="p-3 bg-muted/30 rounded-lg">
                          <div className="flex items-center gap-2 mb-2">
                            <span className="text-sm font-medium text-foreground">
                              {comment.user_name}
                            </span>
                            <span className="text-xs text-muted-foreground">
                              {new Date(comment.created_at).toLocaleDateString()}
                            </span>
                          </div>
                          <p className="text-sm text-foreground">{comment.comment}</p>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </ScrollArea>
            </>
          )}
        </DialogContent>
      </Dialog>
    </DashboardLayout>
  );
}
